import {
    Flex,
    Text,
    Stack,
    Input,
    IconButton,
    Box,
} from '@chakra-ui/react'
import { ArrowBackIcon, EditIcon, CheckIcon } from '@chakra-ui/icons'
import { useLocation, useNavigate } from 'react-router-dom'
import Header from '../../../../../app/components/Header'
import { format } from 'date-fns'
import { useEffect, useRef, useState } from 'react'
import html2pdf from 'html2pdf.js'
import { EnergyConsumptionReportPdf } from '../EnergyConsumptionReportPdf'
import { toast } from 'react-toastify'
import { useGetBillingReportMutation, useGetEnergyConsumptionReportMutation } from '../../../api/reportsApi'
import { formatDateString, getReportNameById } from '../../../../../utils/helpers'
import { BillingReportPdf } from '../BillingReportPdf'

export default function Preview() {
    const location = useLocation()
    const navigate = useNavigate()
    const [reportData, setReportData] = useState<any>()
    const formData = location.state?.formData
    const [getEnergyConsumptionReport, { isLoading: isEnergyConsumptionReportLoading }] =
        useGetEnergyConsumptionReportMutation()
    const [getBillingReport, { isLoading: isBillingReportLoading }] =
        useGetBillingReportMutation()

    const defaultFormattedDateRange = `${format(
        new Date(formData.startDate),
        'dd MMM yyyy'
    )} - ${format(new Date(formData.endDate), 'dd MMM yyyy')}`

    const [isEditing, setIsEditing] = useState(false)
    const [reportName, setReportName] = useState(defaultFormattedDateRange)
    const [isDownloading, setIsDownloading] = useState(false)
    const hasFetchedRef = useRef(false)

    useEffect(() => {
        const fetchData = async () => {
            try {
                switch (formData.selectedReportType) {
                    case 1:
                        const energyData = await getEnergyConsumptionReport({
                            start: formatDateString(formData.startDate),
                            end: formatDateString(formData.endDate),
                            device_ids: formData.selectedDevices,
                            group_ids: formData.selectedGroups,
                        }).unwrap()

                        setReportData(energyData)
                        break
                    case 3:
                        const billingData = await getBillingReport({
                            start: formatDateString(formData.startDate),
                            end: formatDateString(formData.endDate),
                            device_ids: formData.selectedDevices,
                            group_ids: formData.selectedGroups,
                            price: formData.ibexPrice,
                        }).unwrap()
                        setReportData(billingData)
                        break
                }
            } catch (_error) {
                toast.error(`${_error?.data?.message || 'Failed to generate report.'}`, {
                    style: { padding: '10px 20px' },
                    isLoading: false,
                    autoClose: 3000,
                })

                navigate('/reports/generate', { state: { formData } })
            }
        }

        if (!hasFetchedRef.current) {
            hasFetchedRef.current = true
            fetchData()
        }
    }, [formData, getEnergyConsumptionReport, getBillingReport, navigate])


    const handleClick = async () => {
        const component = document.getElementById('pdf-container')
        if (!component) return

        const options = {
            margin: 0,
            filename: `${reportName}.pdf`,
            pagebreak: {
                mode: 'avoid-all',
                before: '#reportBody',
            },
        }

        const applyStyles = (selector: string, styles: Record<string, string>) => {
            const elements = component.querySelectorAll(selector) as unknown as HTMLElement[]
            elements.forEach((el) => {
                Object.assign(el.style, styles)
            })
            return elements
        }

        const elementAdjustments = [
            { selector: '.locationName', styles: { transform: 'translate(0%, -35%)' } },
            { selector: '.cardTitle', styles: { transform: 'translate(0%, -40%)' } },
            { selector: '.arrowIconClass', styles: { transform: 'translate(0%, 15%)' } },
            { selector: '.groupName', styles: { transform: 'translate(0%, -35%)' } },
            { selector: '.groupPercentage', styles: { transform: 'translate(0%, -35%)' } },
            { selector: '.dot', styles: { marginTop: '15px' } },
            { selector: '.donutChart', styles: { transform: 'translate(0%, -25%)' } },
            { selector: '.fromToDate', styles: { transform: 'translate(0%, 5%)' } },
            { selector: '.table', styles: { transform: 'translate(0%, 0%)' } },
            { selector: '.tableRows', styles: { transform: 'translate(0%, -35%)' } },
            { selector: '.trendChart', styles: { transform: 'translate(0%, 5%)' } },
            { selector: '.moveLeft', styles: { marginRight: '20px' } },
            { selector: '.moveRight', styles: { marginLeft: '20px' } },
            { selector: '.down', styles: { transform: 'translate(0%, 2%)' } },
            { selector: '.up', styles: { transform: 'translate(0%, -25%)' } },
            { selector: '.legendValue', styles: { transform: 'translate(0%, -40%)' } },
            {
                selector: '.card-component',
                styles: {
                    backgroundColor: '#ffffff',
                    boxShadow: 'none',
                    borderRadius: '12px',
                },
            },
            {
                selector: '.consumption-by-location',
                styles: {
                    backgroundColor: '#ffffff',
                    boxShadow: 'none',
                    borderRadius: '12px',
                },
            },
            {
                selector: '.locations',
                styles: {
                    backgroundColor: '#ffffff',
                    boxShadow: 'none',
                    borderRadius: '12px',
                },
            },
        ]
        const originalStyles = elementAdjustments.map(({ selector, styles }) => ({
            selector,
            elements: applyStyles(selector, styles),
            styles,
        }))

        const pdfBlob = await html2pdf().set(options).from(component).outputPdf('blob')
        originalStyles?.forEach(({ elements, styles }) => {
            elements?.forEach((el) => {
                Object.keys(styles).forEach((styleKey) => {
                    el.style[styleKey as any] = ''
                })
            })
        })

        return pdfBlob
    }

    const handleDownload = async () => {
        const toastId = toast.info('Downloading report...', {
            style: { padding: '10px 20px' },
            autoClose: false,
            isLoading: true,
            progress: undefined,
        })
        setTimeout(() => {
            setIsDownloading(true)
        }, 0)

        try {
            const pdfBlob = await handleClick()
            const pdfUrl = URL.createObjectURL(pdfBlob)
            const a = document.createElement('a')
            a.href = pdfUrl
            a.download = `${reportName}.pdf`
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)

            toast.update(toastId, {
                render: 'The report has been downloaded successfully.',
                type: 'success',
                isLoading: false,
                autoClose: 3000,
            })
        } catch (error) {
            toast.update(toastId, {
                render: 'Failed to download report!',
                type: 'success',
                isLoading: false,
                autoClose: 3000,
            })
        } finally {
            setIsDownloading(false)
        }
    }

    return (
        <Stack spacing={5}>
            <Header>
                <Flex align="flex-start" gap={2}>
                    <Box pt={1}>
                        <ArrowBackIcon
                            boxSize={4}
                            _hover={{ cursor: 'pointer' }}
                            onClick={() => {
                                navigate('/reports/generate', {
                                    state: { formData },
                                })
                            }}
                        />
                    </Box>
                    {isEditing ? (
                        <Flex align="center" gap={2}>
                            <Input
                                value={reportName}
                                onChange={(e) => setReportName(e.target.value)}
                                size="sm"
                                width="auto"
                            />
                            <IconButton
                                aria-label="Save date range"
                                icon={<CheckIcon />}
                                size="sm"
                                onClick={() => {
                                    if (!reportName.trim()) {
                                        setReportName(defaultFormattedDateRange)
                                    }
                                    setIsEditing(false)
                                }}
                                colorScheme="gray"
                                variant="ghost"
                                _focus={{ boxShadow: 'none' }}
                            />
                        </Flex>
                    ) : (
                        <Box lineHeight="1.2">
                            <Flex align="center" gap={2}>
                                <Text fontWeight="bold" fontSize="lg">
                                    {reportName}
                                </Text>
                                <IconButton
                                    aria-label="Edit date range"
                                    icon={<EditIcon />}
                                    size="sm"
                                    _focus={{ boxShadow: 'none' }}
                                    variant="ghost"
                                    onClick={() => setIsEditing(true)}
                                />
                            </Flex>
                            <Text fontSize="sm" mt="2px">
                                {getReportNameById(formData.selectedReportType)}
                            </Text>
                        </Box>
                    )}
                </Flex>
            </Header>

            {/* Body */}
            {formData.selectedReportType === 1 &&
                <EnergyConsumptionReportPdf
                    data={reportData}
                    isLoading={isEnergyConsumptionReportLoading}
                    formData={formData}
                    startDate={formData.startDate}
                    endDate={formData.endDate}
                    reportName={reportName}
                    isDownloading={isDownloading}
                    handleDownload={handleDownload}
                    handleGenerate={handleClick}
                />}
            {formData.selectedReportType === 3 &&
                <BillingReportPdf
                    fromDate={formData.startDate}
                    toDate={formData.endDate}
                    data={reportData}
                    reportName={reportName}
                    formData={formData}
                    price={formData.ibexPrice}
                    isLoading={isBillingReportLoading}
                    isDownloading={isDownloading}
                    handleDownload={handleDownload}
                    handleGenerate={handleClick}
                />
            }
        </Stack>
    )
}
